import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { LocalStorageService } from '../../core/services/local-storage.service';

interface DeviceVerifyResponse {
  userCode: string;
  expiresAt: string;
  isValid: boolean;
}

@Component({
  selector: 'device-authorize',
  templateUrl: './device-authorize.component.html',
  styleUrls: ['./device-authorize.component.scss'],
  standalone: false
})
export class DeviceAuthorizeComponent implements OnInit {
  userCode: string = '';
  isLoading: boolean = false;
  isVerifying: boolean = false;
  isAuthorizing: boolean = false;
  isAuthorized: boolean = false;
  errorMessage: string = '';
  codeVerified: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) {}

  ngOnInit(): void {
    const codeFromUrl = this.route.snapshot.queryParamMap.get('user_code');
    if (codeFromUrl) {
      this.userCode = codeFromUrl.toUpperCase();
      this.verifyCode();
    }
  }

  onCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');

    if (value.length > 4 && !value.includes('-')) {
      value = value.slice(0, 4) + '-' + value.slice(4);
    }

    if (value.length > 9) {
      value = value.slice(0, 9);
    }

    this.userCode = value;
    input.value = value;
  }

  verifyCode(): void {
    if (!this.userCode || this.userCode.length < 9) {
      this.errorMessage = 'Please enter a valid 8-character code.';
      return;
    }

    this.isVerifying = true;
    this.errorMessage = '';

    const url = `${environment.apiInfo.apiBaseUrl}api/Auth/device/verify?userCode=${encodeURIComponent(this.userCode)}`;

    this.http.get<DeviceVerifyResponse>(url).subscribe({
      next: (response) => {
        this.isVerifying = false;
        if (response.isValid) {
          this.codeVerified = true;
        } else {
          this.errorMessage = 'This code is invalid or has expired.';
        }
      },
      error: (error) => {
        this.isVerifying = false;
        this.errorMessage = error.error?.message || error.error || 'Invalid or expired code. Please check and try again.';
      },
    });
  }

  authorizeDevice(): void {
    const authData = this.localStorageService.authorizationData;
    if (!authData?.token) {
      // Must be an absolute URL: the login page prefixes non-admin/tpo returnUrls with '/admin/',
      // which would send a root-level route like this one to the wildcard login route.
      const returnUrl = `${window.location.origin}/device-authorize?user_code=${encodeURIComponent(this.userCode)}`;
      this.router.navigate(['/login'], {
        queryParams: { returnUrl },
      });
      return;
    }

    this.isAuthorizing = true;
    this.errorMessage = '';

    const url = `${environment.apiInfo.apiBaseUrl}api/Auth/device/verify?userCode=${encodeURIComponent(this.userCode)}`;

    this.http.post<{ succeeded: boolean }>(url, {}).subscribe({
      next: (response) => {
        this.isAuthorizing = false;
        if (response.succeeded) {
          this.isAuthorized = true;
        } else {
          this.errorMessage = 'Failed to authorize the device. Please try again.';
        }
      },
      error: (error) => {
        this.isAuthorizing = false;
        this.errorMessage = error.error?.message || error.error || 'Failed to authorize the device. Please try again.';
      },
    });
  }

  resetForm(): void {
    this.userCode = '';
    this.codeVerified = false;
    this.isAuthorized = false;
    this.errorMessage = '';
  }
}
